import { useState, type FormEvent } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import type { SettingsSupplierRow } from '../../types';
import { SUPPLIER_MODAL_LABELS as LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';

interface SupplierModalProps {
  supplier?: SettingsSupplierRow;
  onClose: () => void;
  onSave: (name: string, email: string) => Promise<boolean>;
}

export default function SupplierModal({ supplier, onClose, onSave }: SupplierModalProps) {
  useModalHistory(onClose);
  const [name, setName] = useState(supplier?.name ?? '');
  const [email, setEmail] = useState(supplier?.email ?? '');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);

  const isCreate = !supplier;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(LABELS.nameRequired);
      return;
    }
    setSaving(true);
    const ok = await onSave(name.trim(), email);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={isCreate ? LABELS.createTitle : LABELS.editTitle} confirmLabel={LABELS.save} confirmFormId="supplier-form" confirmDisabled={saving} confirmLoading={saving} testId="supplier-modal">
      <form id="supplier-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <TextField
            label={LABELS.nameLabel}
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
            required
            autoFocus
            fullWidth
            className="mt-2"
            error={!!nameError}
            helperText={nameError || undefined}
          />
          <TextField
            label={LABELS.emailLabel}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">
            {LABELS.cancel}
          </Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
